import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React from 'react';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { HelpCircle } from 'lucide-react';
import { NumericInputSize, sizeClasses, formatValue, NumericFormat } from './types';

interface SliderNumericFieldProps {
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  prefix?: string;
  label?: string;
  helperText?: string;
  tooltip?: string;
  showInput?: boolean;
  size?: NumericInputSize;
  format?: NumericFormat;
  precision?: number;
  disabled?: boolean;
  className?: string;
}

export function SliderNumericField({
  value,
  onChange,
  min = 0,
  max = 100,
  step = 1,
  suffix,
  prefix,
  label,
  helperText,
  tooltip,
  showInput = true,
  size = 'md',
  format = 'decimal',
  precision = 1,
  disabled = false,
  className = '',
}: SliderNumericFieldProps) {
  useLanguage();
  const sizes = sizeClasses[size];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(e.target.value) || 0;
    const clampedValue = Math.min(max, Math.max(min, newValue));
    onChange(clampedValue);
  };

  return (
    <div className={cn("space-y-2", disabled && "opacity-50 pointer-events-none", className)}>
      {(label || showInput) && (
        <div className="flex items-center justify-between">
          {label && (
            <div className="flex items-center gap-1.5">
              <Label className="text-sm font-medium">{label}</Label>
              {tooltip && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-[200px] text-xs">
                      {tooltip}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          )}
          
          {showInput && (
            <div className="flex items-center gap-1">
              {prefix && <span className="text-xs text-muted-foreground">{prefix}</span>}
              <Input
                type="number"
                value={value}
                onChange={handleInputChange}
                min={min}
                max={max}
                step={step}
                className={cn(sizes.input, "text-right")}
                disabled={disabled}
                aria-label={label || translateInterfaceText("Valor numérico")}
              />
              {suffix && <span className="text-xs text-muted-foreground w-6">{suffix}</span>}
            </div>
          )}
        </div>
      )}
      
      <Slider
        value={[value]}
        onValueChange={([v]) => onChange(v)}
        min={min}
        max={max}
        step={step}
        className="w-full"
        disabled={disabled}
        aria-label={label || translateInterfaceText("Ajustar valor")}
      />
      
      {helperText && (
        <p className="text-xs text-muted-foreground animate-in fade-in-0 duration-200">
          {helperText}
        </p>
      )}
    </div>
  );
}
